<template>
  <v-card>
    <v-row class="mx-0">
      <v-col cols="4">
        <v-row
          class="align-center justify-end mx-0"
          style="gap: 1rem;"
        >
          <select-project
            v-model="project"
            show-select-all
            multiple
            :loading="isLoading"
          />
          <file-button
            accept=".json"
            :loading="isLoading"
            @file-content="onAccountFileRead"
            @clear="accounts = []"
          />
          <v-btn
            color="primary"
            fab
            icon
            :loading="isLoading"
            :disabled="!accounts.length || !isValid"
            @click="onBulkCreate"
          >
            <v-icon>
              mdi-upload
            </v-icon>
          </v-btn>
        </v-row>
      </v-col>

      <v-col
        class="ml-auto text-right"
        cols="1"
      >
        <v-btn
          color="primary"
          fab
          icon
          :disabled="isLoading"
          @click="helpDialog = true"
        >
          <v-icon>
            mdi-help-circle-outline
          </v-icon>
        </v-btn>
      </v-col>
    </v-row>

    <v-row class="mx-0">
      <v-data-table
        class="flex-grow-1 mt-2"
        :items="accounts"
        :headers="headers"
        height="calc(100vh - 195px)"
        :footer-props="{ 'items-per-page-options': [50, 100, -1] }"
      >
        <template #item="{ item, index }">
          <account-row
            :account="item"
            :identifier-counts="identifierCounts"
          />
        </template>
      </v-data-table>
    </v-row>

    <v-dialog
      v-model="helpDialog"
      max-width="600"
    >
      <v-card>
        <v-card-title>
          Help
        </v-card-title>
        <v-card-text>
          <p>
            This is a tool to help you create multiple accounts for a project at once. In order to be able to submit the accounts, make sure that all phones, logins and emails are unique
          </p>
          <p>
            The input must be a JSON file with an array of records in the following format:
            <br>
          </p>
          <p
            v-html="JSON.stringify(HELP_SAMPLE_RECORD, null, 3)"
            style="white-space: pre-line;"
          />

          <p>
            <strong>currency</strong> must be in the <strong>ISO 4217</strong> format
            <br>
            <strong>country</strong> must be in the <strong>ISO 3166-1 alpha-2</strong> format
            <br>
            Profile data will be populated automatically
          </p>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog
      v-model="resultDialog"
      max-width="1200"
    >
      <v-data-table
        :items="createdAccounts"
        :headers="resultHeaders"
      >
        <template #item.entity_id="{value}">
          {{ projectNameById[value] }}
        </template>
        <template
          v-for="header in resultHeaders.slice(1, -1)"
          #[`item.${header.value}`]="{item}"
        >
          <v-text-field
            v-model="item[header.value]"
            hide-details
          />
        </template>

        <template #item.action="{item}">
          <v-btn
            v-if="item.error"
            icon
            color="primary"
            :loading="isLoading"
            @click="$emit('retry', { account: item, entity_id: +item.entity_id, })"
          >
            <v-icon>
              refresh
            </v-icon>
          </v-btn>

          <v-tooltip
            v-if="item.error"
            bottom
          >
            <template #activator="{ on }">
              <v-btn
                icon
                color="error"
                :loading="isLoading"
                v-on="on"
              >
                <v-icon>
                  info
                </v-icon>
              </v-btn>
            </template>

            {{ item.error }}
          </v-tooltip>
        </template>
      </v-data-table>
    </v-dialog>
  </v-card>
</template>

<script>
import AccountRow from './AccountRow.vue'
import FileButton from '@/components/core/FileButton.vue'
import SelectProject from '@/components/core/SelectProject/SelectProject.vue'

const HELP_SAMPLE_RECORD = {
  login: 'TestEurCanada',
  email: 'testEurCanada@test.com',
  password: 'qwerty123',
  currency: 'EUR',
  country: 'CA',
  phone: '232425385385',
}

const COMMON_HEADERS = [
  { text: 'Login', value: 'login' },
  { text: 'Email', value: 'email' },
  { text: 'Password', value: 'password' },
  { text: 'Currency', value: 'currency' },
  { text: 'Country', value: 'country' },
  { text: 'Phone', value: 'phone' },
]

export default {
  props: {
    isLoading: Boolean,
    projectNameById: {
      type: Object,
      required: true,
    },
    createdAccounts: {
      type: Array,
      required: true,
    },
  },
  components: {
    FileButton,
    SelectProject,
    AccountRow,
  },
  data() {
    return {
      accounts: [],
      headers: COMMON_HEADERS,
      resultHeaders: [
        { text: 'Project', value: 'entity_id' },
        ...COMMON_HEADERS,
        { text: '', value: 'action' },
      ],
      helpDialog: false,
      resultDialog: false,
      project: [],
      HELP_SAMPLE_RECORD,
      hasBeenSubmitted: false,
    }
  },

  computed: {
    identifierCounts() {
      const result = {
        email: {},
        phone: {},
        login: {},
      }

      for (const { email, phone, login } of this.accounts) {
        if (!result.email[email]) {
          result.email[email] = 0
        }
        if (!result.phone[phone]) {
          result.phone[phone] = 0
        }
        if (!result.login[login]) {
          result.login[login] = 0
        }

        result.email[email]++
        result.phone[phone]++
        result.login[login]++
      }

      return result
    },

    isValid() {
      if (!this.project) {
        return
      }

      return Object.keys(this.identifierCounts).every(key => {
        return !Object.values(this.identifierCounts[key]).some(v => v !== 1)
      })
    }
  },

  methods: {
    onAccountFileRead(content) {
      try {
        this.accounts = JSON.parse(content)
        this.hasBeenSubmitted = false
      } catch {
        this.$showError('Invalid JSON format')
      }
    },

    onBulkCreate() {
      this.hasBeenSubmitted = true // flag for error display
      this.$emit('create', {
        accounts: this.accounts,
        by_entities: this.project
      })
    }
  },

  watch: {
    createdAccounts(newList) {
      if (!!newList.length) {
        this.resultDialog = true
      }
    }
  },
}
</script>
